import { useState, useEffect } from 'react'
import Swal from 'sweetalert2'
import bettingConfigService from '../../services/bettingConfigService'

// Per-market caps (owner 2026-10-26): one NAMED market of a sport gets its own max rate, beating
// its group column (and any league row) everywhere in that sport — e.g. only "Tied Match" at 20
// while the rest of Match Other stays at 10. 0 = no cap for that market; Remove = back to the
// group's cap. Server: max_odds_market_caps + BettingConfigService.getMaxOddsRule (the slip
// mirrors it). Fancy markets are never capped, so only odds markets are offered.

const fmt = (v) => (Number(v) > 0 ? Number(v).toFixed(2) : 'no limit')

function MaxOddsMarketCaps({ sports, maxValue, limitedOversSportId, onChanged }) {
  const [sportId, setSportId] = useState('')
  const [marketName, setMarketName] = useState('')
  const [maxOdds, setMaxOdds] = useState('')
  const [testMaxOdds, setTestMaxOdds] = useState('')
  const [names, setNames] = useState([])
  const [saving, setSaving] = useState(false)

  const Toast = Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 })
  const isCricket = Number(sportId) === Number(limitedOversSportId)

  // Market names actually bet on in this sport (last 90 days), as suggestions.
  useEffect(() => {
    setNames([])
    if (!sportId) return
    bettingConfigService.getMaxOddsMarketNames(sportId)
      .then((r) => setNames(r.success ? r.names || [] : []))
      .catch(() => setNames([]))
  }, [sportId])

  const rows = sports.flatMap((s) => (s.markets || []).map((m) => ({ ...m, sport_id: s.sport_id, sport: s.name })))

  const inRange = (v) => v === '' || (Number(v) >= 0 && Number(v) <= maxValue)

  const handleSave = async (e) => {
    e.preventDefault()
    if (!sportId || !marketName.trim()) {
      Toast.fire({ icon: 'error', title: 'Pick a sport and type the market name' })
      return
    }
    if (!inRange(maxOdds) || !inRange(testMaxOdds)) {
      Toast.fire({ icon: 'error', title: `Max odds must be between 0 and ${maxValue}` })
      return
    }
    setSaving(true)
    try {
      const r = await bettingConfigService.saveMaxOddsMarketCap({
        sport_id: Number(sportId),
        market_name: marketName.trim(),
        max_odds: Number(maxOdds) || 0,
        test_max_odds: isCricket ? Number(testMaxOdds) || 0 : 0,
      })
      if (!r.success) throw new Error(r.error || r.message || 'Save failed')
      Toast.fire({ icon: 'success', title: `${r.market_name}: ${fmt(r.max_odds)}` })
      setMarketName('')
      setMaxOdds('')
      setTestMaxOdds('')
      onChanged?.()
    } catch (error) {
      Toast.fire({ icon: 'error', title: error.response?.data?.error || error.message })
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (row) => {
    setSportId(String(row.sport_id))
    setMarketName(row.market_name)
    setMaxOdds(String(row.max_odds))
    setTestMaxOdds(String(row.test_max_odds || ''))
  }

  const handleRemove = async (row) => {
    const ok = await Swal.fire({
      title: `Remove ${row.market_name}?`,
      text: `${row.sport} ${row.market_name} goes back to its market group's cap.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Remove',
    })
    if (!ok.isConfirmed) return
    try {
      await bettingConfigService.deleteMaxOddsMarketCap(row.id)
      Toast.fire({ icon: 'success', title: `${row.market_name}: follows its group` })
      onChanged?.()
    } catch (error) {
      Toast.fire({ icon: 'error', title: error.response?.data?.error || error.message })
    }
  }

  return (
    <div className="card">
      <div className="card-body">
        <h4 className="card-title mb-1">Per-market caps</h4>
        <p className="text-muted font-size-12 mb-3">
          Give <b>one market</b> of a sport its own limit, e.g. only <b>Tied Match</b> or only
          <b> Over/Under 2.5</b>. It beats that market&apos;s column above in every league of the sport.
          0 = no limit for that market. <b>Remove</b> puts it back on its column&apos;s limit.
          Saving a market that is already listed updates it.
        </p>

        <form className="row g-2 align-items-end mb-3" onSubmit={handleSave}>
          <div className="col-md-3">
            <label className="font-size-12 mb-0">Sport</label>
            <select className="form-control form-control-sm" value={sportId} onChange={(e) => setSportId(e.target.value)}>
              <option value="">Select sport…</option>
              {sports.map((s) => (
                <option key={s.sport_id} value={s.sport_id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div className="col-md-4">
            <label className="font-size-12 mb-0">Market name</label>
            <input
              className="form-control form-control-sm"
              list="max-odds-market-names"
              placeholder={sportId ? 'Type or pick a market…' : 'Select a sport first'}
              value={marketName}
              maxLength={100}
              onChange={(e) => setMarketName(e.target.value)}
              disabled={!sportId}
            />
            <datalist id="max-odds-market-names">
              {names.map((n) => <option key={n} value={n} />)}
            </datalist>
          </div>
          <div className="col-md-2">
            <label className="font-size-12 mb-0">{isCricket ? 'Oneday & T20 max' : 'Max odds'}</label>
            <input type="number" min="0" max={maxValue} step="0.01" className="form-control form-control-sm"
              placeholder="0 = no limit" value={maxOdds} onChange={(e) => setMaxOdds(e.target.value)} />
          </div>
          {isCricket && (
            <div className="col-md-2">
              <label className="font-size-12 mb-0">Test max</label>
              <input type="number" min="0" max={maxValue} step="0.01" className="form-control form-control-sm"
                placeholder="0 = no limit" value={testMaxOdds} onChange={(e) => setTestMaxOdds(e.target.value)} />
            </div>
          )}
          <div className="col-md-1">
            <button type="submit" className="btn btn-primary btn-sm w-100" disabled={saving}>
              {saving ? '…' : 'Save'}
            </button>
          </div>
        </form>

        <div className="table-responsive">
          <table className="table table-sm table-bordered mb-0">
            <thead>
              <tr>
                <th>Sport</th>
                <th>Market</th>
                <th className="text-right">Max odds</th>
                <th className="text-right">Test max</th>
                <th className="text-center" style={{ width: 150 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={5} className="text-center text-muted">No per-market caps yet</td></tr>
              ) : rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.sport}</td>
                  <td>{row.market_name}</td>
                  <td className="text-right">{fmt(row.max_odds)}</td>
                  <td className="text-right">{Number(row.sport_id) === Number(limitedOversSportId) ? fmt(row.test_max_odds) : '-'}</td>
                  <td className="text-center">
                    <button type="button" className="btn btn-outline-primary btn-sm mr-1" onClick={() => handleEdit(row)}>Edit</button>
                    <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => handleRemove(row)}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default MaxOddsMarketCaps
